import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { API_URL } from '@/lib/constants'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Eye, EyeOff, Key, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

export function ApiTab() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery(
    trpc.userRouter.getApiSecret.queryOptions(),
  )

  const { mutateAsync: regenerate, isPending: isRegenerating } = useMutation(
    trpc.userRouter.regenerateApiSecret.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.userRouter.getApiSecret.queryKey() })
      },
    }),
  )

  const [showSecret, setShowSecret] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false)

  const handleCopy = useCallback(async () => {
    if (!data?.apiSecret)
      return
    await navigator.clipboard.writeText(data.apiSecret)
    setCopied(true)
    toast.success(t('auth.dashboard.apiCopied'))
    setTimeout(() => setCopied(false), 2000)
  }, [data?.apiSecret, t])

  const handleRegenerate = useCallback(async () => {
    await regenerate()
    setShowSecret(true)
    toast.success(t('auth.dashboard.apiRegenerated'))
  }, [regenerate, t])

  const maskedSecret = data?.apiSecret
    ? `${data.apiSecret.slice(0, 14)}${'*'.repeat(20)}...${data.apiSecret.slice(-4)}`
    : ''

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="size-5" />
            {t('auth.dashboard.apiTitle')}
          </CardTitle>
          <CardDescription>
            {t('auth.dashboard.apiDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              { }
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auth.dashboard.apiSecretLabel')}</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded-md border bg-muted/50 px-3 py-2 font-mono text-xs">
                    {showSecret ? data?.apiSecret : maskedSecret}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setShowSecret(!showSecret)}
                    title={showSecret ? 'Hide' : 'Show'}
                  >
                    {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={handleCopy}
                    title={t('auth.dashboard.apiCopyButton')}
                  >
                    {copied ? <Check className="size-4 text-green-500" /> : <Copy className="size-4" />}
                  </Button>
                </div>
              </div>

              { }
              <div className="flex items-center justify-end border-t pt-4">
                <Button
                  variant="destructive"
                  onClick={() => setShowRegenerateDialog(true)}
                  disabled={isRegenerating}
                  className="gap-2"
                >
                  {isRegenerating ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RefreshCw className="size-4" />
                  )}
                  {t('auth.dashboard.apiRegenerateButton')}
                </Button>
              </div>

              { }
              <div className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-800/30 dark:bg-sky-950/20">
                <h3 className="text-sm font-semibold text-sky-700 dark:text-sky-300">
                  {t('auth.dashboard.apiUsageTitle')}
                </h3>
                <p className="text-xs text-sky-600 dark:text-sky-400">
                  {t('auth.dashboard.apiUsageDescription')}
                </p>
                <div className="space-y-2">
                  <div className="rounded bg-gray-900 p-3 text-xs text-gray-100">
                    <p className="text-gray-400"># Discover available endpoints</p>
                    <p>curl {API_URL}/api/_introspect \</p>
                    <p className="pl-4">-H &quot;Authorization: Bearer YOUR_API_SECRET&quot;</p>
                  </div>
                  <p className="text-xs text-sky-600 dark:text-sky-400">
                    {t('auth.dashboard.apiIntrospectHint')}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auth.dashboard.apiRegenerateButton')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('auth.dashboard.apiRegenerateConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRegenerate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('auth.dashboard.apiRegenerateButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
